import { Component, inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { DecimalPipe, NgClass } from "@angular/common";
import { MarketService } from "../../../../../../../shared/src/lib/services/market.service";
import {
  ApexChart,
  ChartComponent,
  ApexDataLabels,
  ApexLegend,
  ApexAxisChartSeries,
  ApexXAxis,
  ApexYAxis,
  ApexGrid,
  ApexPlotOptions,
  NgApexchartsModule,
} from "ng-apexcharts";
import { MatFormField } from "@angular/material/form-field";
import { MatOption } from "@angular/material/autocomplete";
import { MatSelect } from "@angular/material/select";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { MatIcon } from "@angular/material/icon";
import { MarketData, MarketSnapshot, Runner } from "../../../../../../../shared/src/lib/types/market.type";
import { takeUntil } from "rxjs/operators";

interface Row {
  price: number;
  toBack?: number;
  toLay?: number;
  traded?: number;
}

interface PricePoint {
  low: number | null;
  high: number | null;
}

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  grid: ApexGrid;
  colors: string[];
  legend: ApexLegend;
};

@Component({
  selector: "app-market-liquidity",
  imports: [NgApexchartsModule, MatFormField, MatOption, MatSelect, ReactiveFormsModule, DecimalPipe, NgClass, MatIcon],
  templateUrl: "./market-liquidity.component.html",
  styleUrl: "./market-liquidity.component.scss",
})
export class MarketLiquidityComponent implements OnInit, OnDestroy {
  @ViewChild("chart") chart!: ChartComponent;
  readonly destroy$ = new Subject<void>();
  form: FormGroup;
  activatedRoute = inject(ActivatedRoute);
  marketService = inject(MarketService);
  marketSnapshot: MarketSnapshot | null = null;
  market: MarketData | null = null;
  public chartOptions: Partial<ChartOptions> | any = {
    chart: {
      type: "line",
      height: 400,
      stacked: false,
      toolbar: { show: false },
    },
    stroke: {
      width: [1.5, 0],
      curve: "straight",
      colors: ["#b30000"],
    },
    fill: {
      type: ["solid", "solid"],
      colors: ["transparent", "#000"],
    },
    colors: ["#b30000", "#000"],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "65%",
        borderRadius: 0,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      labels: { show: false },
      axisTicks: { show: false },
      axisBorder: { show: false },
    },
    yaxis: [
      {
        seriesName: "Price",
        title: {
          text: "Price",
          style: {
            color: "#b30000",
          },
        },
        labels: {
          style: {
            colors: "#b30000",
          },
        },
      },
      {
        seriesName: "Volume",
        opposite: true,
        title: {
          text: "Volume",
          style: {
            color: "#000",
          },
        },
        labels: {
          style: {
            colors: "#000",
          },
        },
      },
    ],
    grid: {
      borderColor: "#e0e0e0",
    },
    title: {
      text: "Price/Volume over time",
      align: "center",
    },
    legend: {
      show: false,
    },
  };
  tableRows: { back: Row[]; lay: Row[] } = { back: [], lay: [] };
  snapshotRunners: Runner[] = [];
  pricePoints: PricePoint = { low: 0, high: 0 };

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      selectionId: ["", [Validators.required]],
      marketId: [""],
    });
  }

  get selectionId() {
    return this.form.get("selectionId");
  }

  get marketId() {
    return this.form.get("marketId");
  }

  ngOnInit() {
    this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.getMarket(params["marketId"]);
      this.getMarketSnapshot(params["marketId"], params["selectionId"]);
      this.form.patchValue({ selectionId: Number(params["selectionId"]), marketId: params["marketId"] });
    });

    this.selectionId?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selectionId) => {
      this.handleSelectionChange(selectionId);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentRunner() {
    const selectionId = this.selectionId?.value;
    const runner = this.marketSnapshot?.r?.find((r) => r.id === selectionId);
    if (runner) {
      return {
        lastPriceTraded: runner.lpt,
        totalMatched: runner.ex.tvs.reduce((acc, val) => acc + val, 0),
      };
    }

    return null;
  }

  handleSelectionChange(selectionId: string) {
    const runner = this.snapshotRunners?.find((r: Runner) => String(r?.id) === String(selectionId));
    if (runner) {
      this.tableRows = this.buildRunnerTable(runner);
      const { back, lay } = this.tableRows;
      this.pricePoints = this.findTradedBoundaries(back, lay);
      const tvp = runner.ex.tvp;
      const tvs = runner.ex.tvs;
      const categories = tvp.map((_, i) => i + 1); // Just index if no timestamp

      this.chartOptions = {
        ...this.chartOptions,
        series: [
          {
            name: "Price",
            type: "line",
            data: tvp,
          },
          {
            name: "Volume",
            type: "column",
            data: tvs,
          },
        ],
        xaxis: {
          ...this.chartOptions.xaxis,
          categories,
        },
      };
    }
  }

  findTradedBoundaries(backs: Row[], lays: Row[]): PricePoint {
    let low: number | null = null;
    let high: number | null = null;

    // First try to find low in backs
    for (let i = 0; i < backs.length; i++) {
      if (backs[i].traded !== undefined) {
        const noTradedBefore = backs.slice(0, i).every((b) => b.traded === undefined);
        if (noTradedBefore) {
          low = backs[i].price;
          break;
        }
      }
    }

    // If not found in backs, search in lays
    if (low === null) {
      for (let i = 0; i < lays.length; i++) {
        if (lays[i].traded !== undefined) {
          const noTradedBefore = lays.slice(0, i).every((l) => l.traded === undefined);
          if (noTradedBefore) {
            low = lays[i].price;
            break;
          }
        }
      }
    }

    // First try to find high in lays
    for (let i = 0; i < lays.length; i++) {
      if (lays[i].traded !== undefined) {
        const noTradedAfter = lays.slice(i + 1).every((l) => l.traded === undefined);
        if (noTradedAfter) {
          high = lays[i].price;
          break;
        }
      }
    }

    // If not found in lays, search in backs
    if (high === null) {
      for (let i = 0; i < backs.length; i++) {
        if (backs[i].traded !== undefined) {
          const noTradedAfter = backs.slice(i + 1).every((b) => b.traded === undefined);
          if (noTradedAfter) {
            high = backs[i].price;
            break;
          }
        }
      }
    }

    return { low, high };
  }

  buildRunnerTable(runner: Runner): { back: Row[]; lay: Row[] } {
    const { abp, abs, alp, als, tvp, tvs } = runner.ex;
    const backMap: Map<number, Row> = new Map();
    const layMap: Map<number, Row> = new Map();

    const findClosest = (target: number, prices: number[]): number => {
      return prices.reduce((prev, curr) => (Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev));
    };

    abp.forEach((price, i) => {
      const row = backMap.get(price) || { price };
      row.toBack = abs[i];
      backMap.set(price, row);
    });

    alp.forEach((price, i) => {
      const row = layMap.get(price) || { price };
      row.toLay = als[i];
      layMap.set(price, row);
    });

    tvp.forEach((price, i) => {
      const backRow = backMap.get(price);
      const layRow = layMap.get(price);
      if (backRow) {
        backRow.traded = tvs[i];
        backMap.set(price, backRow);
      } else if (layRow) {
        layRow.traded = tvs[i];
        layMap.set(price, layRow);
      } else {
        const backClosest = findClosest(price, abp);
        const layClosest = findClosest(price, alp);

        const backDiff = Math.abs(price - backClosest);
        const layDiff = Math.abs(price - layClosest);

        if (backDiff < layDiff) {
          backMap.set(price, { price, traded: tvs[i] });
        } else {
          layMap.set(price, { price, traded: tvs[i] });
        }
      }
    });

    const back = Array.from(backMap.values()).sort((a, b) => a.price - b.price);
    const lay = Array.from(layMap.values()).sort((a, b) => a.price - b.price);

    return {
      back,
      lay,
    };
  }

  getMarket(id: string) {
    this.marketService
      .fetchMarket({ id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response?.data) {
            this.market = response?.data;
          }
        },
      });
  }

  getMarketSnapshot(marketId: string, runnerId: string) {
    const mId = `bf_${marketId}`;
    const qParams = { market_ids: mId, statuses: "2,3", return_snapshot: "Y" };
    this.marketService
      .fetchMarketStatus(qParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response) {
            const { snapshot } = response || {};
            this.marketSnapshot = snapshot[mId];
            this.snapshotRunners = snapshot[mId]?.r;
            if (this.snapshotRunners?.length > 0) {
              this.handleSelectionChange(runnerId);
            }
          }
        },
      });
  }
}
